import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import QuestionPreview from "../QuestionPreview";
import styles from "./styles";
import TimeRangeDropDown from "../TimeRangeDropdown";
import "date-fns";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { compareAsc } from "date-fns";
import useWindowSize from "../WindowSizeHook";
import { cloneDeep } from "lodash";
import moment from "moment";
import { datePickerError } from "../../constants";
import { useTranslation } from "react-i18next";
import { getTranslation } from "../../../locales";

const useStyles = makeStyles((props) => styles(props));

const AnswerDate = (props) => {
  const { t } = useTranslation("surveyTaker");
  const styleTheme = useTheme();
  const styles = useStyles({ ...props, styleTheme });
  const [answer, setAnswer] = useState({});
  const [helperText, setHelperText] = useState("");
  const dateErrorFlag = useRef(false);
  const { isMobile } = useWindowSize();
  const [maskValue, setMaskValue] = useState("__/__/____");

  useEffect(() => {
    if (props.value && Object.keys(props.value).length !== 0) {
      let dateObj = cloneDeep(props.value);
      dateObj.date = dateObj.date
        ? moment(dateObj.date, props.format.date.toUpperCase()).toDate()
        : ""; // convert string to date object
      updateAnswer(dateObj);
    }
    const renderMask = (format) => {
      if (format === "yyyy/mm/dd") {
        return "____/__/__";
      } else if (format === "yyyy-mm-dd") {
        return "____-__-__";
      } else {
        return "__/__/____";
      }
    };
    setMaskValue(renderMask(props.format.date));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.value && Object.keys(props.value).length !== 0) {
      let dateObj = cloneDeep(props.value);
      dateObj.date = dateObj.date
        ? moment(dateObj.date, props.format.date.toUpperCase()).toDate()
        : ""; // convert string to date object
      setAnswer(dateObj);
    }
  }, [props.value]);

  const updateAnswer = (answerObj) => {
    setAnswer(answerObj);
    if (answerObj && Object.keys(answerObj).length !== 0) {
      const answerClone = cloneDeep(answerObj);
      if (
        answerClone.date &&
        answerClone.date != "Invalid Date" &&
        props.format.date
      ) {
        answerClone.date = moment(answerClone.date).format(
          props.format.date.toUpperCase()
        );
      }
      props.handleChange(answerClone);
    }
  };

  const validateForDays = (date) => {
    if (date && props.customSelection.days && date != "Invalid Date") {
      let excludeDays = props.customSelection.days.includes(
        parseInt(date.getDay())
      );
      if (!excludeDays) {
        dateErrorFlag.current = true;
        handleDateError("range");
        return "Invalid Date";
      } else {
        dateErrorFlag.current = false;
        return date;
      }
    } else {
      return date;
    }
  };

  const validateForRange = (date) => {
    if (props.customSelection.date && date != "Invalid Date") {
      let endDate = props.customSelection.date.before;
      let startDate = props.customSelection.date.after;
      let validRange = {
        startDate: 1,
        endDate: 1,
      };
      if (endDate) {
        validRange.endDate = compareAsc(new Date(endDate), date);
      }
      if (startDate) {
        validRange.startDate = compareAsc(date, new Date(startDate));
      }

      if (validRange.endDate == -1 || validRange.startDate == -1) {
        dateErrorFlag.current = true;
        handleDateError("range");
        return "Invalid Date";
      } else {
        dateErrorFlag.current = false;
        return date;
      }
    } else {
      return date;
    }
  };

  const handleDateChange = (dateInput) => {
    let answerTemp = cloneDeep(answer);
    if (dateInput) {
      let date = new Date(new Date(dateInput).setHours(0, 0, 0, 0));
      let valid;
      valid = validateForDays(date); // Validate Days
      if (valid != "Invalid Date") {
        valid = validateForRange(date); // Validate Date Range
      }
      answerTemp = { ...answerTemp, date: valid };
    } else {
      answerTemp = { ...answerTemp, date: "" };
    }

    updateAnswer(answerTemp);
  };

  const handleChangeTime = (e, val, menuList) => {
    let valExist = Array.isArray(menuList)
      ? menuList.find((ele) => ele == val)
      : 1;
    if (valExist) {
      updateAnswer({ ...answer, time: val });
    }
  };

  const handleDisableDates = (date) => {
    if (props.customSelection.days) {
      let excludeDays = props.customSelection.days.includes(date.getDay());
      return !excludeDays;
    } else {
      return false;
    }
  };

  const handleDateError = (reason) => {
    if (reason) {
      if (
        dateErrorFlag.current == true &&
        (props.customSelection.days || props.customSelection.date)
      ) {
        setHelperText(
          getTranslation(props.currentLang, datePickerError.rangeErrorMsg)
        );
      } else {
        setHelperText(
          getTranslation(props.currentLang, datePickerError.invalidDateMsg)
        );
      }
      updateAnswer({ ...answer, date: "Invalid Date" });
    } else {
      setHelperText("");
    }
  };

  return (
    <div className={styles.questionWrapper}>
      <QuestionPreview
        question={props.question}
        required={props.required}
        helperText={props.helperText}
      />
      <div className={styles.answer}>
        {props.format.date && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {
              <DatePicker
                onError={handleDateError}
                label={t("surveyTaker:date")}
                value={answer.date ? answer.date : null}
                maxDate={
                  props.customSelection.date &&
                  props.customSelection.date.before
                    ? new Date(props.customSelection.date.before)
                    : new Date("2100-01-01")
                }
                minDate={
                  props.customSelection.date && props.customSelection.date.after
                    ? new Date(props.customSelection.date.after)
                    : new Date("1900-01-01")
                }
                variant={isMobile ? "dialog" : "inline"}
                inputFormat={props.format.date.replace(/mm/i, "MM")}
                mask={maskValue}
                margin="normal"
                id="Date"
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    helperText={helperText}
                    className={styles.datePicker}
                    autoComplete="off"
                  />
                )}
                KeyboardButtonProps={{
                  "aria-label": t("surveyTaker:changeDate"),
                }}
                InputAdornmentProps={{ position: "start" }}
                shouldDisableDate={handleDisableDates}
                InputProps={{ "data-testid": "date-picker-input" }}
                inputVariant="outlined"
                defaultCalendarMonth={
                  props.customSelection.date && props.customSelection.date.after
                    ? new Date(props.customSelection.date.after)
                    : props.customSelection.date &&
                      props.customSelection.date.before
                    ? new Date(props.customSelection.date.before)
                    : null
                }
              />
            }
          </LocalizationProvider>
        )}

        {props.format.time && (
          <TimeRangeDropDown
            label={t("surveyTaker:time")}
            twentyFourHour={props.format.time === "24hrs"}
            handleChangeTime={handleChangeTime}
            timeValue={answer.time}
            showRange={
              props.customSelection.time
                ? [
                    props.customSelection.time.after,
                    props.customSelection.time.before,
                  ]
                : []
            }
            currentLang={props.currentLang}
          />
        )}
      </div>
    </div>
  );
};

export default AnswerDate;
